import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Heading } from '@ruby-labs/ui-core-kit';

import { BaseContext } from 'context/base.context';

import { useLocalStorage } from 'hooks/use-local-storage.hook';
import { useSaveAnswers } from 'hooks/use-save-answers.hook';

import { UNIT_SYSTEM } from 'constants/body-height-weight';
import { MultipleLocalStorageKeys } from 'constants/local-storage';
import { UserGeolocationKeys } from 'constants/user-geolocation';

import { NextButton } from 'components/next-button';
import { BodyHeightWeightInputs } from 'components/body-height-weight-inputs';

const convertDataForIntakeForm = (data: { unit: string, weight: string }) => {
    return `Weight: ${data.weight} ${data.unit === UNIT_SYSTEM.metric ? 'kg' : 'lbs'}`;
};

export const BodyWeight = () => {
    const { t } = useTranslation();
    const saveAnswers = useSaveAnswers();

    const { pageConfiguration } = useContext(BaseContext);

    const bodyHeightWeightKeys = MultipleLocalStorageKeys.bodyHeightWeightKeys;
    const countryCode = localStorage.getItem(UserGeolocationKeys.countryCode) as string;

    const [pageError, setPageError] = useState(false);

    const [disabledButtonState, setDisabledButtonState] = useState(true);
    const [errors, setError] = useState({});

    const handleClick = () => {
        if (disabledButtonState) {
            return;
        }

        const data = {
            unit: bodyHeightWeightUnitSystem,
            weight:
                bodyHeightWeightUnitSystem === UNIT_SYSTEM.imperial ? bodyWeightLbs : bodyWeightKg,
        };

        saveAnswers({
            key: 'body-weight',
            itemQuestion: { question: t('body-weight-title'), value: convertDataForIntakeForm(data) },
        });
        pageConfiguration.handleOnPageComplete(data);
    };

    const { pageValue: bodyHeightWeightUnitSystem } =
        useLocalStorage({
            key: bodyHeightWeightKeys.unitSystem,
            defaultValue: countryCode === 'US' ? UNIT_SYSTEM.imperial : UNIT_SYSTEM.metric,
        });

    const { pageValue: bodyWeightLbs } = useLocalStorage({
        key: bodyHeightWeightKeys.weightLbs,
        defaultValue: '',
    });

    const { pageValue: bodyWeightKg } = useLocalStorage({
        key: bodyHeightWeightKeys.weightKg,
        defaultValue: '',
    });

    useEffect(() => {

        setPageError(() => {
            // @ts-ignore
            return Object.keys(errors).some((key) => errors[key]);
        });

    }, [JSON.stringify(errors)]);

    useEffect(() => {
        const btnState = bodyHeightWeightUnitSystem === UNIT_SYSTEM.imperial
            ? pageError || !bodyWeightLbs
            : pageError || !bodyWeightKg;

        setDisabledButtonState(btnState);

    }, [
        bodyHeightWeightUnitSystem,
        bodyWeightLbs,
        bodyWeightKg,
        pageError,
    ]);

    return (
        <>
            <Heading title={t('body-weight-title')} subtitle={t('body-weight-subtitle')}/>

            <BodyHeightWeightInputs heightInput={false} idealWeightInput={false} setInputError={setError}
                                    pageError={pageError} />

            <NextButton onClick={handleClick} disabled={disabledButtonState} />
        </>
    );
};
